import { validateVerisoulSession, VerisoulSession } from 'src/apis/verisoul';
import UserModel from 'src/models/UserModel';
import environmentStore from 'src/stores/EnvironmentStore';

declare global {
  interface Window {
    Verisoul?: {
      reinitialize: () => Promise<void>;
      session: () => Promise<{ session_id: string }>;
    };
  }
}

export const isDevEnv =
  process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV?.includes('preview') || false;

export const clearVerisoulSession = async (user: UserModel): Promise<void> => {
  try {
    if ((window && !window.Verisoul) || isDevEnv) return;

    await window.Verisoul?.reinitialize();
  } catch (error) {
    user.trackError('Failed to reset Verisoul session', error);
  }
};

export const getVerisoulSessionId = async (user: UserModel): Promise<string> => {
  try {
    if (isDevEnv || (window && !window.Verisoul)) return '';

    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Verisoul session timeout')), 10000)
    );

    const { session_id } = (await Promise.race([window.Verisoul!.session(), timeout])) || {};

    if (!session_id) {
      throw new Error('Verisoul session ID not found');
    }

    return session_id;
  } catch (error) {
    user.trackError('Failed to get Verisoul session', error);
    return '';
  }
};

const isVerisoulBlocked = async (session: VerisoulSession) => {
  if (session?.lists && session.lists.length > 0) {
    const blockList = environmentStore.verisoulBlockGroups;
    return session.lists.some((group) => blockList.includes(group));
  }
  return false;
};

export const checkVerisoul = async (user: UserModel) => {
  const session = await validateVerisoulSession(user);

  if (!session) {
    return false;
  }

  const isBlocked = await isVerisoulBlocked(session);

  const verisoulDecision = session.decision === 'Fake' ? 'fake' : 'real';

  user.events.trackAction('verisoul', {
    score: session.accountScore,
    decision: isBlocked ? 'group-blocked' : verisoulDecision
  });

  return isBlocked;
};
